// File: src/StuckDriverHelper.js
// OOOSH Driver Verification - Stuck Driver Safety Net
// SESSION 92: Reusable component that detects stuck drivers and offers help
//
// Two triggers:
//   1. failureCount >= 3 (attempt-based — most common trigger)
//   2. 5 minutes since mount with no successful completion (time-based backup)
//
// Shows a gentle amber inset (Oct 2026 redesign: hf-note-amber, see hireform.css) with:
//   - "Try refreshing" button (calls smartRoute via page reload)
//   - "Email us" link (pre-filled mailto with driver details)
//
// Usage:
//   const [failureCount, setFailureCount] = useState(0);
//   // In catch blocks: setFailureCount(prev => prev + 1);
//   <StuckDriverHelper failureCount={failureCount} pageName="POA1" email={email} />

import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import reportError from './utils/errorReporter';
import { HELP_EMAIL, HELP_PHONE_DISPLAY, HELP_PHONE_TEL } from './ui/HireFormUI';

const IDLE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
const FAILURE_THRESHOLD = 3;

const StuckDriverHelper = ({ failureCount = 0, pageName = 'Unknown', email = '' }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showExpanded, setShowExpanded] = useState(false);
  const [triggeredBy, setTriggeredBy] = useState(null); // 'failures' or 'timeout'
  const timerRef = useRef(null);
  const hasLoggedRef = useRef(false);

  // Extract jobId from URL (avoids needing it as a prop)
  const getJobId = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('job') || 'unknown';
    } catch {
      return 'unknown';
    }
  };

  // Trigger 1: Failure count threshold
  useEffect(() => {
    if (failureCount >= FAILURE_THRESHOLD && !showBanner) {
      console.log(`🆘 Stuck driver detected: ${failureCount} failures on ${pageName}`);
      setShowBanner(true);
      setTriggeredBy('failures');

      // Log to error reporting (only once)
      if (!hasLoggedRef.current) {
        hasLoggedRef.current = true;
        reportError({
          page: pageName,
          error: `Stuck driver: ${failureCount} failed attempts`,
          severity: 'warning',
          email,
          additionalInfo: `Job: ${getJobId()}`
        });
      }
    }
  }, [failureCount, pageName, email, showBanner]);

  // Trigger 2: Idle timeout (backup)
  useEffect(() => {
    timerRef.current = setTimeout(() => {
      if (!showBanner) {
        console.log(`🆘 Stuck driver detected: idle timeout on ${pageName}`);
        setShowBanner(true);
        setTriggeredBy('timeout');

        if (!hasLoggedRef.current) {
          hasLoggedRef.current = true;
          reportError({
            page: pageName,
            error: `Stuck driver: idle for 5+ minutes (${failureCount} attempts)`,
            severity: 'warning',
            email,
            additionalInfo: `Job: ${getJobId()}`
          });
        }
      }
    }, IDLE_TIMEOUT_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  // Build mailto link with pre-filled details
  const getMailtoLink = () => {
    const jobId = getJobId();
    const subject = encodeURIComponent(`Driver document help - Job ${jobId}`);
    const body = encodeURIComponent(
      `Hi,\n\n` +
      `I'm having trouble uploading my ${getDocumentName()} through the online system.\n\n` +
      `My details:\n` +
      `- Email: ${email}\n` +
      `- Job reference: ${jobId}\n` +
      `- Document: ${getDocumentName()}\n\n` +
      `Please find the document attached to this email.\n\n` +
      `Thank you`
    );
    return `mailto:${HELP_EMAIL}?subject=${subject}&body=${body}`;
  };

  // Human-friendly document name based on page
  const getDocumentName = () => {
    switch (pageName) {
      case 'POA1Page': return 'Proof of Address #1';
      case 'POA2Page': return 'Proof of Address #2';
      case 'DVLAPreviewPage': return 'DVLA driving record check';
      default: return 'document';
    }
  };

  // Handle refresh - reload page, smart routing will place them correctly
  const handleRefresh = () => {
    window.location.reload();
  };

  // Don't render anything until triggered
  if (!showBanner) return null;

  return (
    <div className="hf-note hf-note-amber hf-stuck">
      {/* Header - always visible, opens the help below */}
      <button
        type="button"
        className="hf-stuck-toggle"
        onClick={() => setShowExpanded(!showExpanded)}
        aria-expanded={showExpanded}
      >
        <span>Having trouble?</span>
        {showExpanded ? <ChevronUp size={18} aria-hidden="true" /> : <ChevronDown size={18} aria-hidden="true" />}
      </button>

      {showExpanded && (
        <div className="hf-stuck-body">
          {/* Step 1: Try refreshing */}
          <p>
            {triggeredBy === 'failures'
              ? "Your document isn't going through. Refreshing the page often fixes it, so try that first."
              : 'This seems to be taking a while. Try refreshing the page to carry on.'}
          </p>
          <button type="button" className="hf-btn-secondary" onClick={handleRefresh}>
            Refresh the page
          </button>

          {/* Step 2: Email us */}
          <p style={{ marginTop: 6 }}><strong>Still not working?</strong></p>
          <p>
            Email us your {getDocumentName()} and we&rsquo;ll sort it out by hand. Please attach the
            document to the email.
          </p>
          <a className="hf-btn-secondary" href={getMailtoLink()}>
            Email us your document
          </a>
          <p>
            Or email <a href={`mailto:${HELP_EMAIL}`}>{HELP_EMAIL}</a>, or call us
            on <a href={HELP_PHONE_TEL}>{HELP_PHONE_DISPLAY}</a>.
          </p>
        </div>
      )}
    </div>
  );
};

export default StuckDriverHelper;